import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { AlertPopup } from '../common/AlertPopup';
import StreetAutocompleteInput from '../common/StreetAutocompleteInput';
import { fetchPicklists } from '../common/constants';
import { AddAccountModal } from '../AccountManagement';
import { formatPhoneNumber } from './phoneUtils';
import { US_STATES, CONTACT_TYPE_OPTIONS } from './contactConstants';
import { 
  toUSStateCode, 
  formatCountryDisplay, 
  validateEmail, 
  validateNettlescsEmail,
  buildComparableFromSelected 
} from './contactUtils';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import './AddContactModal.css';

export function UpdateContactModal({ isOpen, onClose, sectionType, opportunityName, contact, onContactUpdated }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [accountSearch, setAccountSearch] = useState('');
  const [isEmailDropdownVisible, setIsEmailDropdownVisible] = useState(false);
  const [isAccountDropdownVisible, setIsAccountDropdownVisible] = useState(false);
  const [emailContacts, setEmailContacts] = useState([]);
  const [accountContacts, setAccountContacts] = useState([]);
  const [noEmailFound, setNoEmailFound] = useState(false);
  const [selectedContact, setSelectedContact] = useState({
    Email: '',
    Email_Alternate__c: '',
    Personal_Email__c: '',
    FirstName: '',
    LastName: '',
    Title: '',
    Department: '',
    Type: '',
    GMB_Type__c: '',
    GMB_Region__c: '',
    Phone: '',
    MobilePhone: '',
    Fax: '',
    MailingAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    },
    OtherAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    },
    GMB_Account_Billing_State_Short_Code__c: '',
    AccountName: '',
    AccountId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', isError: false });
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    isChecking: false,
    exists: false
  });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [mode, setMode] = useState('add'); // 'add' or 'edit'
  const [isAccountSearchLoading, setIsAccountSearchLoading] = useState(false);
  const [noAccountFound, setNoAccountFound] = useState(false);
  const [showWrongAccountNote, setShowWrongAccountNote] = useState(false);
  const [showAddAccountButton, setShowAddAccountButton] = useState(false);
  const [isContactSearchLoading, setIsContactSearchLoading] = useState(false);

  // Debounce state for search functions
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [accountSearchTimeout, setAccountSearchTimeout] = useState(null);

  const [typeOptions, setTypeOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);

  // Keep a snapshot of the originally fetched contact to detect edits
  const originalComparableRef = useRef(null);

  useEffect(() => {
    fetchPicklists()
      .then(({ typeOptions, regionOptions }) => {
        setTypeOptions(typeOptions);
        setRegionOptions(regionOptions);
      })
      .catch(err => {
        console.error("Failed to load picklists:", err);
      });
  }, []);


  // Ref for search dropdown click-outside handling
  const searchDropdownRef = useRef(null);

  // Handle clicks outside the search dropdown
  useEffect(() => {
    if (!showSearchDropdown) return;

    function handleClickOutside(event) {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchDropdown]);


  // Debounced account search function
  const performAccountSearch = useCallback(async (accountName) => {
    // Check if the original input has at least 3 characters (including spaces)
    if (accountName.length < 3) {
      setIsAccountDropdownVisible(false);
      setAccountContacts([]);
      setIsAccountSearchLoading(false);
      return;
    }

    // If the input consists only of spaces, don't trigger the API
    if (accountName.trim().length === 0) {
      setIsAccountDropdownVisible(false);
      setAccountContacts([]);
      setIsAccountSearchLoading(false);
      return;
    }

    setIsAccountSearchLoading(true);
    try {
      if (!isReady) return;

      // Manually encode the search parameter to ensure spaces are encoded as %20
      const encodedSearch = encodeURIComponent(accountName);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts/name/${encodedSearch}`,
        await getAuthConfig()
      );
      const records = response.data.records;

      if (records.message === "No record found for the given account name") {
        setAccountContacts([]); // Ensure it's set to an empty array
        setIsAccountDropdownVisible(false);
      } else if (records.Id) {
        // Single record found - wrap in array for consistency
        setAccountContacts([records]);
        setIsAccountDropdownVisible(true);
      } else if (Array.isArray(records.records)) {
        // Multiple records found - filter to only show accounts that start with the search term
        const searchTerm = accountName.trim().toLowerCase();
        const filteredRecords = records.records.filter(account =>
          account.Name && account.Name.toLowerCase().startsWith(searchTerm)
        );
        setAccountContacts(filteredRecords);
        setIsAccountDropdownVisible(filteredRecords.length > 0);
      } else {
        // No records found
        setAccountContacts([]);
        setIsAccountDropdownVisible(false);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setIsAccountSearchLoading(false);
    }
  }, [getAuthConfig, isReady]);

  const handleAccountSearch = (e) => {
    const accountName = e.target.value;
    setSelectedContact((prev) => ({ ...prev, AccountName: accountName, AccountId: '', }));

    // Clear existing timeout
    if (accountSearchTimeout) {
      clearTimeout(accountSearchTimeout);
    }

    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      performAccountSearch(accountName);
    }, 1000);

    setAccountSearchTimeout(newTimeout);
  };

  const handleAccountSelect = (account) => {
    setSelectedContact((prev) => {
      // Only auto-fill if we're creating a new contact (mode === 'add')
      // Don't auto-fill if we're editing an existing contact (mode === 'edit')
      const isCreatingNewContact = mode === 'add';
      return {
        ...prev,
        AccountName: account.Name,
        AccountId: account.Id, // This should be the Salesforce Account ID
        // Auto-fill Type and Region from account data only when creating new contact
        Type: isCreatingNewContact ? (account.Type || prev.Type) : prev.Type,
        GMB_Region__c: isCreatingNewContact ? (account.GMB_Region__c || prev.GMB_Region__c) : prev.GMB_Region__c,
        // Auto-fill mailing address from billing address only when creating new contact
        MailingAddress: isCreatingNewContact ? {
          ...prev.MailingAddress,
          street: account.BillingStreet || prev.MailingAddress.street,
          city: account.BillingCity || prev.MailingAddress.city,
          state: account.BillingState || prev.MailingAddress.state,
          postalCode: account.BillingPostalCode || prev.MailingAddress.postalCode,
          country: account.BillingCountry || prev.MailingAddress.country,
        } : prev.MailingAddress,
      };
    });
    setIsAccountDropdownVisible(false);
  };

  const handleEmailSearch = async (e) => {
    const email = e.target.value.trim();
    setAccountSearch(email);

    // Only search and show dropdown if at least 3 characters are typed
    if (email.length < 3) {
      setNoEmailFound(false);
      setEmailContacts([]);
      setIsEmailDropdownVisible(false);
      return;
    }

    // Reset email validation if the main email field matches the new search email
    if (email === selectedContact.Email) {
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
    }

    // Reset fields except email and id
    setSelectedContact((prev) => ({
      ...prev,
      Email: email,
      Id: prev.Id || "",
      Email_Alternate__c: "",
      Personal_Email__c: "",
      FirstName: "",
      LastName: "",
      Title: "",
      Department: "",
      Type: "",
      GMB_Type__c: "",
      GMB_Region__c: "",
      Phone: "",
      MobilePhone: "",
      Fax: "",
      MailingAddress: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      },
      OtherAddress: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      },
      GMB_Account_Billing_State_Short_Code__c: "",
      AccountName: "",
      AccountId: prev.AccountId || "",
    }));
    setMode('add'); // Reset to add mode when fields are cleared

    try {
      if (!isReady) return;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/email/${email}`,
        await getAuthConfig()
      );
      const data = response.data;

      if (data.records?.message === "No record found for the given email" ||
        data.message === "No record found for the given partial email") {
        // No records found - show create new contact form
        setNoEmailFound(true);
        setEmailContacts([]);
        setIsEmailDropdownVisible(false);
      } else if (data.records?.records && Array.isArray(data.records.records) && data.records.records.length > 0) {
        // Multiple records found - show dropdown
        setNoEmailFound(false);
        const contacts = data.records.records;
        setEmailContacts(contacts);
        setIsEmailDropdownVisible(true);
      } else if (data.records && !Array.isArray(data.records) && data.records.Email) {
        const record = data.records;
        setNoEmailFound(false);
        setEmailContacts([record]);
        setIsEmailDropdownVisible(true);
      } else {
        setNoEmailFound(true);
        setEmailContacts([]);
        setIsEmailDropdownVisible(false);
      }
    } catch (error) {
      console.error("Error fetching contact:", error);
      setNoEmailFound(true);
      setEmailContacts([]);
      setIsEmailDropdownVisible(false);
    }
  };

  // Helper function to handle phone number input changes
  const handlePhoneChange = (field, value) => {
    // Remove all non-digit characters for storage
    const cleaned = value.replace(/\D/g, '');
    setSelectedContact((prev) => ({ ...prev, [field]: cleaned }));
  };

  const addToChatMembers = async (email, opportunityUUID) => {
    if (!isReady) return;
    // Only add to chat if email ends with @nettlescs.com
    if (email && opportunityUUID) {
      const isNettlesEmail = email.toLowerCase().endsWith('@nettlescs.com');
      
      if (!isNettlesEmail) {
        console.log('Skipping chat member addition - not a nettlescs.com email:', email);
        return;
      }

      console.log('Attempting to add chat member with data:', {
        opportunityNo: opportunityUUID,
        email: email
      });

      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_BASEURL}/add-chat-member`,
          {
            opportunityNo: opportunityUUID,
            email: email
          },
          await getAuthConfig({
            headers: {
              'Content-Type': 'application/json'
            }
          })
        );
        console.log('Successfully added to chat members');
      } catch (chatError) {
        console.error('Error adding to chat members:', chatError);
        console.error('Error details:', {
          status: chatError.response?.status,
          data: chatError.response?.data,
          message: chatError.message
        });
      }

    } else {
      console.log('Skipping chat member addition - missing data:', {
        hasEmail: !!email,
        hasOpportunityUUID: !!opportunityUUID,
        email,
        opportunityUUID
      });
    }
  };


  const handleUpdateContact = async () => {
    const {
      id,
      Email,
      FirstName,
      LastName,
      AccountId,
      Type,
      GMB_Region__c,
      Title,
      Department,
      Phone,
      MobilePhone,
      Fax,
      Email_Alternate__c,
      Personal_Email__c,
      MailingAddress,
      OtherAddress,
      GMB_Account_Billing_State_Short_Code__c
    } = selectedContact;

    // Extract opportunity UUID from localStorage
    const projectData = JSON.parse(localStorage.getItem('selectedProjectDetails'));
    const opportunityUUID = projectData?.opportunityUUID ||
      projectData?.opportunityNo ||
      projectData?.Opportunity_System_Wide_ID__c;

    if (!opportunityUUID) {
      setAlert({
        show: true,
        message: "Opportunity UUID is required and could not be found.",
        isError: true
      });
      setIsLoading(false);
      return;
    }

    // Check required fields
    if ((!Email?.trim() && !noEmail) || !FirstName?.trim() || !LastName?.trim() || !GMB_Region__c?.trim()) {
      setAlert({
        show: true,
        message: "Please fill all required fields: Email (or check No email exists), First Name, Last Name, and Region.",
        isError: true
      });
      return;
    }

    // Validate nettlescs.com email domain restrictions (only if email is provided)
    if (Email?.trim() && !validateNettlescsEmail(Email, sectionType, selectedGroup)) {
      const isNettlesTeam = sectionType === "Nettles Project Team" ||
        sectionType === "customerContacts" ||
        selectedGroup === "Nettles Project Team" ||
        selectedGroup === "customerContacts";

      const errorMessage = isNettlesTeam
        ? "Only nettlescs.com email addresses are allowed for Nettles Project Team."
        : "Email addresses with nettlescs.com domain are only allowed for Nettles Project Team.";
      setAlert({
        show: true,
        message: errorMessage,
        isError: true
      });
      return;
    }

    // Ensure accountId is present
    if (!AccountId?.trim()) {
      setAlert({
        show: true,
        message: "Please select a valid Account.",
        isError: true
      });
      return;
    }

    // Debug logging to see what values are being sent
    console.log('Update Contact - Type values:', {
      selectedContactType: selectedContact.Type,
      destructuredType: Type,
      isVendor: sectionType === "Vendors",
      contactId: contact?.id,
      selectedContact: selectedContact
    });

    // Prepare payload for the API
    const payload = {
      Group: selectedGroup,
      ContactType: selectedContact.contactType,
      Salutation: "",
      FirstName,
      LastName,
      AccountId,
      Title: Title || "",
      Department: Department || "",
      Phone: Phone || "",
      MobilePhone: MobilePhone || "",
      Fax: Fax || "",
      Email: noEmail ? "" : Email,
      Email_Alternate__c: Email_Alternate__c || "",
      Personal_Email__c: Personal_Email__c || "",
      GMB_Type__c: Type,
      GMB_Region__c,
      MailingStreet: MailingAddress?.street || "",
      MailingCity: MailingAddress?.city || "",
      MailingState: MailingAddress?.state || "",
      MailingPostalCode: MailingAddress?.postalCode || "",
      MailingCountry: MailingAddress?.country || "US",
      GMB_Account_Billing_State_Short_Code__c: GMB_Account_Billing_State_Short_Code__c || "TX",
      OtherStreet: OtherAddress?.street || "",
      OtherCity: OtherAddress?.city || "",
      OtherState: OtherAddress?.state || "",
      OtherPostalCode: OtherAddress?.postalCode || "",
      OtherCountry: OtherAddress?.country || "US",
      Opportunity_UUID__c: opportunityUUID
    };

    // Conditionally include last-modified fields ONLY if something changed from original
    const currentComparable = buildComparableFromSelected({
      ...selectedContact,
      // Ensure we use the latest primitives passed into payload above
      FirstName,
      LastName,
      AccountId,
      Title,
      Department,
      Phone,
      MobilePhone,
      Fax,
      Email: noEmail ? '' : Email,
      Email_Alternate__c,
      Personal_Email__c,
      Type,
      GMB_Region__c,
      MailingAddress,
      OtherAddress,
      GMB_Account_Billing_State_Short_Code__c
    });
    const originalComparable = originalComparableRef.current;
    const hasChanges = !originalComparable || JSON.stringify(currentComparable) !== JSON.stringify(originalComparable);
    if (hasChanges) {
      payload.Project_Site_Contact_Last_Modified_By__c = (window.__currentUserDisplayName || '');
      payload.Project_Site_Contact_Last_Modified_Date__c = new Date().toISOString();
    }

    setIsLoading(true);
    try {
      if (!isReady) return;
      let response;
      if (id) {
        // Editing existing contact: PUT request
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/${id}`,
          payload,
          await getAuthConfig({
            headers: {
              "Content-Type": "application/json"
            }
          })
        );
      } else {
        // Creating new contact: POST request
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts`,
          payload,
          await getAuthConfig({
            headers: {
              "Content-Type": "application/json"
            }
          })
        );
      }

      // Add to chat members ONLY for Nettles Project Team
      const isNettlesTeamForChat = sectionType === "Nettles Project Team" ||
        sectionType === "customerContacts" ||
        selectedGroup === "Nettles Project Team" ||
        selectedGroup === "customerContacts";
      if (isNettlesTeamForChat) {
        await addToChatMembers(Email, opportunityUUID);
      }

      // After successful update/create, fetch the latest contacts
      const contactsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/opportunity/${opportunityUUID}`,
        await getAuthConfig()
      );

      // Update the parent component with the latest contacts
      if (onContactUpdated) {
        onContactUpdated(contactsResponse.data.data);
      }

      // Also trigger a refresh event to ensure all components are updated
      window.dispatchEvent(new CustomEvent('refreshContacts'));

      // Show success state
      setIsSuccess(true);
      setIsLoading(false);

      // Close the modal after a delay to show success state
      setTimeout(() => {
        onClose();
      }, 1200);

    } catch (error) {
      setIsLoading(false);
      console.error("Error saving contact:", error);
      if (error.response) {
        setAlert({
          show: true,
          message: `Failed to save contact! ${error.response.data.message || "Unknown error"}`,
          isError: true
        });
      } else {
        setAlert({
          show: true,
          message: "Failed to save contact due to a network error or invalid response.",
          isError: true
        });
      }
    }
  };

  const handleCreateContact = async () => {
    const {
      FirstName,
      LastName,
      AccountId,
      Title,
      Department,
      Phone,
      MobilePhone,
      Fax,
      Email,
      Email_Alternate__c,
      Personal_Email__c,
      Type,
      GMB_Region__c,
      MailingAddress,
      OtherAddress,
      GMB_Account_Billing_State_Short_Code__c,
    } = selectedContact;

    // Validate required fields
    if (!AccountId || !FirstName || !LastName || (!Email && !noEmail) || !Type || !GMB_Region__c) {
      setAlert({
        show: true,
        message: "Please fill all required fields: Account, First Name, Last Name, Email (or check No email exists), Type, and Region",
        isError: true
      });
      return;
    }

    // Validate nettlescs.com email domain restrictions (only if email is provided)
    if (Email?.trim() && !validateNettlescsEmail(Email, sectionType, selectedGroup)) {
      const isNettlesTeam = sectionType === "Nettles Project Team" ||
        sectionType === "customerContacts" ||
        selectedGroup === "Nettles Project Team" ||
        selectedGroup === "customerContacts";

      const errorMessage = isNettlesTeam
        ? "Only nettlescs.com email addresses are allowed for Nettles Project Team."
        : "Email addresses with nettlescs.com domain are only allowed for Nettles Project Team.";
      setAlert({
        show: true,
        message: errorMessage,
        isError: true
      });
      return;
    }

    // Debug logging to see what values are being sent
    console.log('Create Contact - Type values:', {
      selectedContactType: selectedContact.Type,
      destructuredType: Type,
      selectedContact: selectedContact
    });

    // Prepare payload for Salesforce API
    const salesforcePayload = {
      Salutation: "",
      FirstName,
      LastName,
      AccountId,
      Title: Title || "",
      Department: Department || "",
      Phone: Phone || "",
      MobilePhone: MobilePhone || "",
      Fax: Fax || "",
      Email: noEmail ? "" : Email,
      Email_Alternate__c: Email_Alternate__c || "",
      Personal_Email__c: Personal_Email__c || "",
      GMB_Type__c: Type,
      GMB_Region__c,
      MailingStreet: MailingAddress?.street || "",
      MailingCity: MailingAddress?.city || "",
      MailingState: MailingAddress?.state || "",
      MailingPostalCode: MailingAddress?.postalCode || "",
      MailingCountry: MailingAddress?.country || "US",
      OtherStreet: OtherAddress?.street || "",
      OtherCity: OtherAddress?.city || "",
      OtherState: OtherAddress?.state || "",
      OtherPostalCode: OtherAddress?.postalCode || "",
      OtherCountry: OtherAddress?.country || "US",
      GMB_Account_Billing_State_Short_Code__c: selectedContact.Account_Billing_State?.toUpperCase() || ""
    };

    setIsLoading(true);
    try {
      if (!isReady) return;
      // First create contact in Salesforce
      const salesforceResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts`,
        salesforcePayload,
        await getAuthConfig({ headers: { "Content-Type": "application/json" } })
      );

      // If Salesforce contact creation is successful, create in Projects API
      if (salesforceResponse.data) {
        // Then call handleSubmit to make the Projects API call
        await handleSubmit(new Event('submit'));

        // Add to chat members ONLY for Nettles Project Team
        const isNettlesTeamForChat = sectionType === "Nettles Project Team" ||
          sectionType === "customerContacts" ||
          selectedGroup === "Nettles Project Team" ||
          selectedGroup === "customerContacts";
        if (isNettlesTeamForChat) {
          const projectData = JSON.parse(localStorage.getItem('selectedProjectDetails'));
          const opportunityUUID = projectData?.opportunityUUID ||
            projectData?.opportunityNo ||
            projectData?.Opportunity_System_Wide_ID__c;
          await addToChatMembers(Email, opportunityUUID);
        }

        setIsSuccess(true);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      if (error.response) {
        console.error("Error creating contact:", error.response.data);
        setAlert({
          show: true,
          message: `Failed to create contact! ${error.response.data.message || "Unknown error"}`,
          isError: true
        });
      } else {
        console.error("Error creating contact:", error);
        setAlert({
          show: true,
          message: "Failed to create contact due to a network error or invalid response.",
          isError: true
        });
      }
    }
  };

  const handleContactSelect = (contact) => {
    console.log('Selected contact:', contact);

    // Parse the Name field into FirstName and LastName
    const nameParts = contact.Name ? contact.Name.split(' ') : ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const lockedContactType = localStorage.getItem('selectedContactType');
    const currentContactType = selectedContact.contactType;

    const nextSelected = {
      ...selectedContact,
      Id: contact.Id || contact.id || '',
      id: contact.id || '',
      Email: contact.Email || '',
      Email_Alternate__c: contact.Email_Alternate__c || '',
      Personal_Email__c: contact.Personal_Email__c || '',
      FirstName: firstName,
      LastName: lastName,
      Title: contact.Title || '',
      Department: contact.Department || '',
      Type: contact.GMB_Type__c || '', // Only set if contact has a type, otherwise leave empty
      GMB_Type__c: contact.GMB_Type__c || '',
      GMB_Region__c: contact.GMB_Region__c || '',
      Phone: contact.Phone || '',
      MobilePhone: contact.MobilePhone || '',
      Fax: contact.Fax || '',
      MailingAddress: {
        street: contact.MailingStreet || '',
        city: contact.MailingCity || '',
        state: contact.MailingState || '',
        postalCode: contact.MailingPostalCode || '',
        country: contact.MailingCountry || '',
      },
      OtherAddress: {
        street: contact.OtherStreet || '',
        city: contact.OtherCity || '',
        state: contact.OtherState || '',
        postalCode: contact.OtherPostalCode || '',
        country: contact.OtherCountry || '',
      },
      GMB_Account_Billing_State_Short_Code__c: contact.GMB_Account_Billing_State_Short_Code__c || '',
      AccountName: contact.Account?.Name || '',
      AccountId: contact.AccountId || '',
      // Preserve the locked contact type instead of using the contact's type
      contactType: lockedContactType || currentContactType || contact.contactType || '',
    };
    setSelectedContact(nextSelected);
    // Capture original snapshot when selecting from search results (no separate fetch step)
    setTimeout(() => {
      originalComparableRef.current = buildComparableFromSelected(nextSelected);
    }, 0);
    setAccountSearch(contact.Email || ''); // Sync selected email to input
    setShowSearchDropdown(false); // Close the dropdown
    setFilteredContacts([]); // Clear filtered results
    setNoEmail(!contact.Email); // Set noEmail based on whether contact has email
    setNoEmailFound(false); // <-- Check the 'No email exists' box if no email
    setIsEmailDropdownVisible(false); // Ensure email dropdown is closed
    setMode('edit'); // Set mode to edit when contact is selected
  };

  const handleCloseAlert = () => {
    setAlert({ show: false, message: '', isError: false });
  };

  useEffect(() => {
    if (!isOpen) {
      // Clear the selectedContactType from localStorage when modal closes
      localStorage.removeItem('selectedContactType');

      // Reset all search and dropdown states when modal closes
      setAccountSearch('');
      setAccountContacts([]);
      setEmailContacts([]);
      setNoEmailFound(false);
      setShowSearchDropdown(false);
      setFilteredContacts([]);
      setIsAccountDropdownVisible(false);
      setIsEmailDropdownVisible(false);
      setNoAccountFound(false);
      setShowAddAccountButton(false);
      setShowWrongAccountNote(false);
      setIsAccountSearchLoading(false);

      // Clear any timeouts
      if (accountSearchTimeout) {
        clearTimeout(accountSearchTimeout);
        setAccountSearchTimeout(null);
      }
    }
  }, [isOpen, searchTimeout, accountSearchTimeout]);

  const validateEmailField = async (email) => {
    // If the email matches the search email, don't show any validation
    if (email === accountSearch) {
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
      return;
    }

    if (!email || !validateEmail(email)) {
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
      return;
    }

    setEmailValidation(prev => ({ ...prev, isChecking: true }));
    try {
      if (!isReady) return;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/email/${email}`,
        await getAuthConfig()
      );

      // Check the total field to determine if email exists
      // total === 0 means no records found (email doesn't exist)
      // total > 0 means records found (email exists)
      const exists = response.data.total > 0;

      setEmailValidation({
        isValid: !exists,
        isChecking: false,
        exists: exists
      });
    } catch (error) {
      console.error("Error checking email:", error);
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!isReady) return;
      const projectData = JSON.parse(localStorage.getItem('selectedProjectDetails'));

      // Log the entire projectData to see what's available
      console.log('Project data from localStorage:', projectData);

      // Try different possible field names for the opportunity UUID
      const opportunityUUID = projectData?.opportunityUUID ||
        projectData?.opportunityNo ||
        projectData?.Opportunity_System_Wide_ID__c;

      console.log('Extracted opportunityUUID:', opportunityUUID);

      // Validate that opportunityUUID exists
      if (!opportunityUUID) {
        throw new Error('Invalid Opportunity UUID - not found in project data');
      }

      // Validate AccountId is present
      if (!selectedContact.AccountId) {
        throw new Error('AccountId is required');
      }

      // Validate Type is one of the allowed values
      if (!typeOptions.includes(selectedContact.Type)) {
        throw new Error('Invalid Type value');
      }

      // Debug logging to see what name fields are being sent for update
      console.log('Contact update payload - name fields:', {
        FirstName: selectedContact.FirstName,
        LastName: selectedContact.LastName,
        Type: selectedContact.Type,
        GMB_Type__c: selectedContact.GMB_Type__c
      });

      // Start with the same payload structure as Salesforce API
      const basePayload = {
        Salutation: "",
        FirstName: selectedContact.FirstName,
        LastName: selectedContact.LastName,
        AccountId: selectedContact.AccountId,
        Title: selectedContact.Title || "",
        Department: selectedContact.Department || "",
        Phone: selectedContact.Phone || "",
        MobilePhone: selectedContact.MobilePhone || "",
        Fax: selectedContact.Fax || "",
        Email: noEmail ? "" : selectedContact.Email,
        Email_Alternate__c: selectedContact.Email_Alternate__c || "",
        Personal_Email__c: selectedContact.Personal_Email__c || "",
        GMB_Type__c: selectedContact.Type,
        GMB_Region__c: selectedContact.GMB_Region__c,
        MailingStreet: selectedContact.MailingAddress?.street || "",
        MailingCity: selectedContact.MailingAddress?.city || "",
        MailingState: selectedContact.MailingAddress?.state || "",
        MailingPostalCode: selectedContact.MailingAddress?.postalCode || "",
        MailingCountry: selectedContact.MailingAddress?.country || "US",
        OtherStreet: selectedContact.OtherAddress?.street || "",
        OtherCity: selectedContact.OtherAddress?.city || "",
        OtherState: selectedContact.OtherAddress?.state || "",
        OtherPostalCode: selectedContact.OtherAddress?.postalCode || "",
        OtherCountry: selectedContact.OtherAddress?.country || "US",
        GMB_Account_Billing_State_Short_Code__c: selectedContact.Account_Billing_State?.toUpperCase() || ""
      };

      // Create a payload with the correct field name for the opportunity UUID
      let projectsApiPayload = {
        ...basePayload,
        Opportunity_UUID__c: opportunityUUID,
        Group: selectedGroup,
        ContactType: selectedContact.contactType
      };

      // Conditionally include last-modified fields ONLY if something changed
      const currentComparable = buildComparableFromSelected(selectedContact);
      const originalComparable = originalComparableRef.current;
      const hasChanges = !originalComparable || JSON.stringify(currentComparable) !== JSON.stringify(originalComparable);
      if (hasChanges) {
        projectsApiPayload = {
          ...projectsApiPayload,
          Project_Site_Contact_Last_Modified_By__c: (window.__currentUserDisplayName || ''),
          Project_Site_Contact_Last_Modified_Date__c: new Date().toISOString()
        };
      }

      console.log('Sending payload to API:', JSON.stringify(projectsApiPayload));

      // Make API call to Projects API
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts`,
        projectsApiPayload,
        await getAuthConfig({
          headers: {
            'Content-Type': 'application/json'
          }
        })
      );

      console.log('API response:', response.data);

      // If contact creation is successful, add to chat members
      if (response.data.status === 'success' && selectedContact.Email && !noEmail) {
        try {
          await addToChatMembers(selectedContact.Email, opportunityUUID);
        } catch (chatError) {
          console.error('Error adding to chat members:', chatError);
          // Don't throw error here as contact was created successfully
        }
      }

    } catch (error) {
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Clear timeouts when modal closes
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      if (accountSearchTimeout) {
        clearTimeout(accountSearchTimeout);
        setAccountSearchTimeout(null);
      }

      setAccountSearch('');
      setAccountContacts([]);
      setEmailContacts([]);
      setNoEmailFound(false);
      setIsSuccess(false);
      setNoEmail(false); // Reset noEmail state
      setEmailValidation({
        isValid: false,
        isChecking: false,
        exists: false
      });
      setSelectedContact(prev => ({
        ...prev,
        Email: '',
        Email_Alternate__c: '',
        Personal_Email__c: '',
        FirstName: '',
        LastName: '',
        Title: '',
        Department: '',
        Type: '',
        GMB_Type__c: '',
        GMB_Region__c: '',
        Phone: '',
        MobilePhone: '',
        Fax: '',
        MailingAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        OtherAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        GMB_Account_Billing_State_Short_Code__c: '',
        AccountName: '',
        AccountId: '',
        contactType: '',
      }));
      setMode('add'); // Reset to add mode when modal closes
      setIsAccountSearchLoading(false); // Reset account search loading state
    } else if (sectionType) {
      setSelectedGroup(sectionType);
      if (sectionType === "Vendors") {
        console.log('Vendor section - contact info:', {
          hasContact: !!contact,
          contactId: contact?.id,
          currentType: selectedContact.Type,
          isEditing: contact && contact.id,
          mode: contact && contact.id ? 'edit' : 'add'
        });

        // For vendors, only set Type to "Vendor" if this is a NEW contact
        // If editing an existing contact, DON'T override the Type - let fetchContactDetails handle it
        const isEditingExistingContact = contact && contact.id;
        const isLoadingContactDetails = localStorage.getItem('loadingContactDetails') === 'true';

        // Don't override Type if we're currently loading contact details or if we're editing an existing contact
        if (!isLoadingContactDetails && !isEditingExistingContact) {
          setSelectedContact(prev => ({
            ...prev,
            contactType: "Vendor",
            Type: "" // For new vendor contacts, leave Type empty to show "Choose"
          }));
        } else if (!isLoadingContactDetails && isEditingExistingContact) {
          // For editing existing contacts, just set contactType but don't touch Type
          setSelectedContact(prev => ({
            ...prev,
            contactType: "Vendor"
            // Don't set Type - let it come from the contact data
          }));
        }
        // Set the contact type in localStorage for vendors to lock the field
        localStorage.setItem('selectedContactType', 'Vendor');
        setMode(isEditingExistingContact ? 'edit' : 'add'); // Set mode based on whether editing
      } else {
        // Get the selected contact type from localStorage if available
        const selectedContactType = localStorage.getItem('selectedContactType');
        if (selectedContactType) {
          setSelectedContact(prev => ({
            ...prev,
            contactType: selectedContactType,
            // Only pre-populate Type if we're editing an existing contact
            Type: contact && contact.id ? selectedContactType : ''
          }));
          // Don't clear localStorage here - keep it for reference but don't lock the field
        } else {
          setSelectedContact(prev => ({
            ...prev,
            contactType: '',
            Type: ''
          }));
        }
        setMode('add'); // Set to add mode for new contact
      }
    }
  }, [isOpen, sectionType, contact]);

  // Additional useEffect to ensure Type is preserved for vendor contacts
  useEffect(() => {
    if (sectionType === "Vendors" && contact && contact.id && selectedContact.Type) {
      console.log('Vendor contact Type preservation check:', {
        originalType: selectedContact.Type,
        contactId: contact.id,
        isVendor: sectionType === "Vendors"
      });
    }
  }, [selectedContact.Type, sectionType, contact]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSearchDropdown(false);
        setFilteredContacts([]);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowSearchDropdown(false);
        setFilteredContacts([]);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Remove the old fetchContacts - we'll only search when user types

  // Debounced search function using new Salesforce search API
  const performSearch = useCallback(async (searchTerm) => {
    if (searchTerm.length < 3) {
      setFilteredContacts([]);
      setShowSearchDropdown(false);
      setMode('add'); // Reset to add mode when search is cleared
      return;
    }

    setIsContactSearchLoading(true);
    try {
      if (!isReady) return;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/search/${encodeURIComponent(searchTerm)}`,
        await getAuthConfig()
      );
      const data = response.data;
      
      if (data.records && data.records.length > 0) {
        setFilteredContacts(data.records);
        setShowSearchDropdown(true);
      } else {
        setFilteredContacts([]);
        setShowSearchDropdown(true);
      }
    } catch (error) {
      console.error('Error searching contacts:', error);
      setFilteredContacts([]);
      setShowSearchDropdown(false);
    } finally {
      setIsContactSearchLoading(false);
    }
  }, [getAuthConfig, isReady]);

  const handleSearch = (e) => {
    // Don't allow input changes while API call is in progress
    if (isContactSearchLoading) {
      return;
    }

    const searchTerm = e.target.value;
    setAccountSearch(searchTerm);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search (1 second)
    const newTimeout = setTimeout(() => {
      if (searchTerm.length >= 3) {
        performSearch(searchTerm);
      } else if (searchTerm.length > 0) {
        setFilteredContacts([]);
        setShowSearchDropdown(false);
      }
    }, 1000);

    setSearchTimeout(newTimeout);
  };

  useEffect(() => {
    const fetchContactDetails = async () => {
      if (contact && contact.id) {
        if (!isReady) return;
        setIsLoading(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/${contact.id}`,
            await getAuthConfig()
          );
          if (response.data.status === 'success') {
            const data = response.data.data;
            // Debug logging to see what name fields are being returned
            console.log('Contact data from API:', {
              FirstName: data.FirstName,
              LastName: data.LastName,
              Name: data.Name,
              GMB_Type__c: data.GMB_Type__c,
              ContactType: data.ContactType,
              Group: data.Group,
              MailingStreet: data.MailingStreet,
              MailingCity: data.MailingCity,
              MailingState: data.MailingState,
              MailingPostalCode: data.MailingPostalCode,
              MailingCountry: data.MailingCountry,
              OtherStreet: data.OtherStreet,
              OtherCity: data.OtherCity,
              OtherState: data.OtherState,
              OtherPostalCode: data.OtherPostalCode,
              OtherCountry: data.OtherCountry,
              MailingAddress: data.MailingAddress,
              OtherAddress: data.OtherAddress
            });

            const contactData = {
              id: data.id, // backend integer id
              Id: data.Id, // Salesforce Id
              Email: data.Email || '',
              Email_Alternate__c: data.Email_Alternate__c || '',
              Personal_Email__c: data.Personal_Email__c || '',
              FirstName: data.FirstName || '',
              LastName: data.LastName || '',
              Title: data.Title || '',
              Department: data.Department || '',
              Type: data.GMB_Type__c || '',
              GMB_Type__c: data.GMB_Type__c || '',
              GMB_Region__c: data.GMB_Region__c || '',
              Phone: data.Phone || '',
              MobilePhone: data.MobilePhone || '',
              Fax: data.Fax || '',
              MailingAddress: {
                street: data.MailingStreet || data.MailingAddress?.street || '',
                city: data.MailingCity || data.MailingAddress?.city || '',
                state: data.MailingState || data.MailingAddress?.state || '',
                postalCode: data.MailingPostalCode || data.MailingAddress?.postalCode || '',
                country: data.MailingCountry || data.MailingAddress?.country || 'US',
              },
              OtherAddress: {
                street: data.OtherStreet || data.OtherAddress?.street || '',
                city: data.OtherCity || data.OtherAddress?.city || '',
                state: data.OtherState || data.OtherAddress?.state || '',
                postalCode: data.OtherPostalCode || data.OtherAddress?.postalCode || '',
                country: data.OtherCountry || data.OtherAddress?.country || 'US',
              },
              GMB_Account_Billing_State_Short_Code__c: data.GMB_Account_Billing_State_Short_Code__c || '',
              AccountName: data.AccountName || '',
              AccountId: data.AccountId || '',
              contactType: data.ContactType || '',
              Opportunity_UUID__c: data.Opportunity_UUID__c || ''
            };

            console.log('Setting contact data for vendor:', {
              originalType: data.GMB_Type__c,
              contactType: data.ContactType,
              group: data.Group,
              isVendor: sectionType === "Vendors"
            });

            // Set a flag to indicate we're loading contact details
            localStorage.setItem('loadingContactDetails', 'true');
            setSelectedContact(contactData);
            // Capture original comparable snapshot for change detection
            // Delay one tick to ensure state has settled (avoid transient diffs)
            setTimeout(() => {
              originalComparableRef.current = buildComparableFromSelected(contactData);
            }, 0);
            // Clear the flag after setting the data
            setTimeout(() => {
              localStorage.removeItem('loadingContactDetails');
            }, 100);
            setSelectedGroup(data.Group || '');
            setAccountSearch(data.Email || '');
            setNoEmail(!data.Email); // Set noEmail based on whether contact has email
            setMode('edit'); // Set mode to edit when editing existing contact
          } else {
            setAlert({
              show: true,
              message: 'Failed to fetch contact details',
              isError: true
            });
          }
        } catch (error) {
          setAlert({
            show: true,
            message: 'Error fetching contact details',
            isError: true
          });
        } finally {
          setIsLoading(false);
        }
      }
    };
    if (isOpen && contact && contact.id) {
      fetchContactDetails();
    }
  }, [isOpen, contact, isReady]);

  // You can use sectionType to customize the modal based on the section
  const getModalTitle = () => {
    const baseTitle = (() => {
      switch (sectionType) {
        case "Nettles Project Team":
          return "Update Nettles Project Team Contact";
        case "GC/Customer":
          return "Update GC/Customer Contact";
        case "Vendors":
          return "Update Vendor Contact";
        case "Designer/Consultant":
          return "Update Designer/Consultant Contact";
        default:
          return "Update Contact";
      }
    })();

    return opportunityName ? (
      <span>
        {baseTitle} for <span className="text-red-600">{opportunityName}</span>
      </span>
    ) : baseTitle;
  };

  if (!isOpen) return null;

  // Determine if editing an existing contact
  const isEditingExistingContact = Boolean(contact && contact.id);

  const handleClose = () => {
    setIsSuccess(false);
    localStorage.removeItem('selectedContactType');
    onClose();
  };

  const handleSuccessDone = async () => {
    setIsSuccess(false);
    localStorage.removeItem('selectedContactType');
    const projectData = JSON.parse(localStorage.getItem('selectedProjectDetails'));
    const opportunityUUID = projectData?.opportunityUUID ||
      projectData?.opportunityNo ||
      projectData?.Opportunity_System_Wide_ID__c;

    if (opportunityUUID && isReady) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/opportunity/${opportunityUUID}`,
          await getAuthConfig()
        );
        if (onContactUpdated) {
          onContactUpdated(response.data.data);
        }
        onClose();
      } catch (error) {
        console.error("Error fetching updated contacts:", error);
        onClose();
      }
    } else {
      onClose();
    }
  };

  return createPortal(
    <div className="add-contact-modal-overlay">
      {alert.show && (
        <AlertPopup
          message={alert.message}
          onClose={handleCloseAlert}
          isError={alert.isError}
        />
      )}
      <div className="add-contact-modal-sheet">
        <div className="add-contact-modal-header">
          <h2 className="add-contact-modal-title">{getModalTitle()}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="add-contact-modal-close"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
            </svg>
          </button>
        </div>

        <div className={`add-contact-modal-body${isSuccess ? ' add-contact-modal-body--success' : ''}`}>
          {isSuccess ? (
            <>
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2 text-center">
                {noEmailFound ? "Contact Created Successfully!" : "Contact Updated Successfully!"}
              </h2>
            </>
          ) : (
            <div className="max-md:p-0">
              <form
                onSubmit={handleSubmit}
                className="space-y-4 relative"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              >
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
                  </div>
                )}

                <div style={{ display: 'none' }}>
                  <input type="text" autoComplete="chrome-off" />
                  <input type="text" autoComplete="off" />
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">Search Contacts</label>
                  <div ref={searchDropdownRef} className="relative search-container">
                    <div className="flex items-center border rounded-md">
                      <input
                        type="text"
                        value={accountSearch}
                        onChange={handleSearch}
                        placeholder="Search by name, email, or phone (minimum 3 characters)"
                        className={`w-full p-2 border-0 focus:outline-none rounded-md ${isContactSearchLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={isLoading || isContactSearchLoading}
                        autoComplete="new-password"
                        autoCorrect="off"
                        spellCheck="false"
                        data-form-type="other"
                      />
                      {isContactSearchLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                      ) : (
                        <img src={Icon13} alt="Search Icon" className="w-5 h-5 mr-2 cursor-pointer" />
                      )}
                    </div>
                    {accountSearch && accountSearch.length > 0 && accountSearch.length < 3 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Type at least 3 characters to search ({accountSearch.length}/3)
                      </div>
                    )}

                        {showSearchDropdown && filteredContacts.length > 0 && (
                          <div className="absolute top-full left-0 bg-white border rounded-md mt-1 w-full shadow-lg z-10 max-h-[400px] overflow-y-auto max-md:overflow-x-auto add-contact-search-dropdown">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Name</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Account Name</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Mobile Phone</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {filteredContacts.map((contact) => (
                                  <tr
                                    key={contact.Id}
                                    className="hover:bg-gray-100 cursor-pointer"
                                    onClick={() => handleContactSelect(contact)}
                                  >
                                    <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                      <div className="text-sm max-md:text-xs font-medium text-gray-900 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{contact.Name}</div>
                                    </td>
                                    <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                      <div className="text-sm max-md:text-xs text-gray-500 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{contact.Account?.Name || '-'}</div>
                                    </td>
                                    <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                      <div className="text-sm max-md:text-xs text-gray-500 break-all" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{contact.Email}</div>
                                    </td>
                                    <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                      <div className="text-sm max-md:text-xs text-gray-500 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{contact.MobilePhone}</div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {showSearchDropdown && filteredContacts.length === 0 && accountSearch.length >= 3 && (
                          <div className="text-sm text-green-600 mt-1">
                            No matching contacts found. You can create a new one.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1 add-contact-form-grid-2">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Group<span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={selectedGroup}
                          className="w-full p-2 border rounded-md bg-gray-100"
                          disabled
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Contact Type<span className="text-red-500">*</span></label>
                        <select
                          className={`w-full p-2 border rounded-md ${localStorage.getItem('selectedContactType')
                              ? 'bg-gray-100'
                              : ''
                          }`}
                          value={selectedContact.contactType}
                          onChange={(e) =>
                            setSelectedContact(prev => ({ ...prev, contactType: e.target.value }))
                          }
                          disabled={isLoading || localStorage.getItem('selectedContactType')}
                          required
                        >
                          <option value="">Select Contact Type</option>
                          {CONTACT_TYPE_OPTIONS[selectedGroup]?.map((contactType) => (
                            <option key={contactType} value={contactType}>
                              {contactType}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1 add-contact-form-grid-3 add-contact-email-grid">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Email<span className="text-red-500">*</span></label>
                        <div className="relative">
                          <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                            <input
                              type="email"
                              value={selectedContact.Email}
                              className={`w-full p-2 border rounded-md ${emailValidation.exists ? 'border-red-500' :
                                  emailValidation.isValid ? 'border-green-500' : ''
                                }`}
                              onChange={(e) => {
                                setSelectedContact((prev) => ({ ...prev, Email: e.target.value }));
                                setEmailValidation({ isValid: false, isChecking: false, exists: false });
                              }}
                              onBlur={(e) => validateEmailField(e.target.value)}
                              disabled={isLoading || isEditingExistingContact || noEmail}
                              autoComplete="new-password"
                              autoCorrect="off"
                              spellCheck="false"
                              data-form-type="other"
                              required={!noEmail}
                            />
                            <label className="flex items-center gap-2 whitespace-nowrap max-md:whitespace-normal">
                              <input
                                type="checkbox"
                                checked={noEmail}
                                onChange={(e) => {
                                  setNoEmail(e.target.checked);
                                  if (e.target.checked) {
                                    setSelectedContact(prev => ({ ...prev, Email: '' }));
                                  }
                                }}
                                className="rounded border-gray-300"
                                disabled={isLoading}
                              />
                              <span className="text-sm text-gray-600">No email exists</span>
                            </label>
                          </div>

                          {/* Email validation feedback - positioned directly below the primary email input */}
                          {emailValidation.isChecking && (
                            <div className="text-xs text-blue-600 mt-1 flex items-center">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                              Checking email...
                            </div>
                          )}

                          {!emailValidation.isChecking && emailValidation.isValid && (
                            <div className="text-xs text-green-600 mt-1">✓ Email is available</div>
                          )}

                          {!emailValidation.isChecking && emailValidation.exists && (
                            <div className="text-xs text-red-600 mt-1">This email already exists</div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Email - Alternate</label>
                        <input
                          type="email"
                          value={selectedContact.Email_Alternate__c}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, Email_Alternate__c: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Personal Email</label>
                        <input
                          type="email"
                          value={selectedContact.Personal_Email__c}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, Personal_Email__c: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div className="col-span-3 max-md:col-span-1">
                        <div className="flex items-center justify-between mb-1 max-md:flex-col max-md:items-start max-md:gap-2">
                          <label className="block text-sm text-gray-600">Search Account<span className="text-red-500">*</span></label>
                          <button
                            className="text-red-600 text-sm hover:underline whitespace-nowrap max-md:whitespace-normal"
                            onClick={() => setShowAddAccountModal(true)}
                          >
                            + Add Account
                          </button>
                        </div>
                        <div className="relative">
                          <div className="flex items-center relative">
                            <input
                              type="text"
                              value={selectedContact.AccountName}
                              onChange={handleAccountSearch}
                              placeholder="Search by account name"
                              className="w-full p-2 border rounded-md focus:outline-none"
                              disabled={isLoading || isAccountSearchLoading}
                              autoComplete="new-password"
                              autoCorrect="off"
                              spellCheck="false"
                              data-form-type="other"
                            />
                            {isAccountSearchLoading && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-md">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700"></div>
                              </div>
                            )}
                            <img src={Icon13} alt="Search Icon" className="absolute right-2 w-5 h-5 cursor-pointer" />
                          </div>
                          {isAccountDropdownVisible && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-[400px] overflow-y-auto max-md:overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Account Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Phone</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Address</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-md:px-2 max-md:py-1">Website</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {accountContacts.map((account) => (
                                    <tr
                                      key={account.Id}
                                      className="hover:bg-gray-100 cursor-pointer"
                                      onClick={() => handleAccountSelect(account)}
                                    >
                                      <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                        <div className="text-sm max-md:text-xs font-medium text-gray-900 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{account.Name}</div>
                                      </td>
                                      <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                        <div className="text-sm max-md:text-xs text-gray-500 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{formatPhoneNumber(account.Phone)}</div>
                                      </td>
                                      <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                        <div className="text-sm max-md:text-xs text-gray-500 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                          {account.BillingStreet ? (
                                            <>
                                              {account.BillingStreet}
                                              {account.BillingCity && <>, {account.BillingCity}</>}
                                              {account.BillingState && <>, {account.BillingState}</>}
                                              {account.BillingPostalCode && <> {account.BillingPostalCode}</>}
                                            </>
                                          ) : '-'}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 max-md:px-2 max-md:py-1">
                                        <div className="text-sm max-md:text-xs text-gray-500 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                          {account.Website ? (
                                            <a
                                              href={account.Website.startsWith('http') ? account.Website : `http://${account.Website}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {account.Website}
                                            </a>
                                          ) : '-'}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">First Name<span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={selectedContact.FirstName}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, FirstName: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Last Name<span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={selectedContact.LastName}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, LastName: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Title</label>
                        <input
                          type="text"
                          value={selectedContact.Title}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, Title: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Department</label>
                        <input
                          type="text"
                          value={selectedContact.Department}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, Department: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={selectedContact.Type}
                          onChange={(e) =>
                            setSelectedContact(prev => ({ ...prev, Type: e.target.value }))
                          }
                          style={{ color: selectedContact.Type === "" ? "gray" : "black" }}
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        >
                          <option value="" disabled>Choose</option>
                          {typeOptions.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Region <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={selectedContact.GMB_Region__c}
                          onChange={(e) =>
                            setSelectedContact({ ...selectedContact, GMB_Region__c: e.target.value })
                          }
                          style={{ color: selectedContact.GMB_Region__c === "" ? "gray" : "black" }}
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        >
                          <option value="" disabled>Choose</option>
                          {[
                            "AR-Entire State",
                            "IA-Entire State",
                            "KS-East, Manhattan/Topeka/Lawrence",
                            "KS-KC Metro",
                            "KS-West/W of Manhattan",
                            "KS-Wichita Metro",
                            "LA-New Orleans Metro",
                            "LA-North, N of Alexandria",
                            "LA-South, Alexandria & South",
                            "MO-Columbia/Jeff City Area",
                            "MO-KC Metro",
                            "MO-Springfield Metro",
                            "MO-St Joseph",
                            "MO-St Louis Metro",
                            "NE-Entire State",
                            "OK-East, E of I-35",
                            "OK-OKC Metro",
                            "OK-Tulsa Metro",
                            "OK-West, W of I-35",
                            "TX-Austin Metro",
                            "TX-DFW Metro",
                            "TX-Houston Metro",
                            "TX-North East, Waco/Longview",
                            "TX-San Antonio Metro",
                            "TX-South East, Lufkin/Bryan CS/Beaumont",
                            "TX - Valley, Victoria/Corpus/McAllen",
                            "TX - West, Abilene/Lubbock",
                            "Other",
                          ]
                            .concat(selectedContact.GMB_Region__c || [])
                            .filter((item, index, arr) => arr.indexOf(item) === index) // Ensure uniqueness
                            .map((region) => (
                              <option key={region} value={region}>
                                {region}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Phone</label>
                        <input
                          type="text"
                          value={formatPhoneNumber(selectedContact.Phone)}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) => handlePhoneChange('Phone', e.target.value)}
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Mobile</label>
                        <input
                          type="text"
                          value={formatPhoneNumber(selectedContact.MobilePhone)}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) => handlePhoneChange('MobilePhone', e.target.value)}
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Fax</label>
                        <input
                          type="text"
                          value={selectedContact.Fax}
                          className="w-full p-2 border rounded-md"
                          onChange={(e) =>
                            setSelectedContact((prev) => ({ ...prev, Fax: e.target.value }))
                          }
                          disabled={isLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 max-md:grid-cols-1 add-contact-form-grid-2">
                      <div>
                        <h3 className="block text-sm text-gray-600 mb-1">Mailing Address</h3>
                        <StreetAutocompleteInput
                          value={selectedContact.MailingAddress?.street || ''}
                          onChange={(text) => setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: { ...prev.MailingAddress, street: text },
                          }))}
                          onSelectSuggestion={(s) => setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: { ...prev.MailingAddress, street: s.description || s.main_text || '' },
                          }))}
                          onPlaceDetails={(d) => setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: {
                              ...prev.MailingAddress,
                              street: [d.street_number, d.route].filter(Boolean).join(' ') || prev.MailingAddress.street,
                              city: d.city || prev.MailingAddress.city,
                              state: d.country_code === 'US' ? (toUSStateCode(d.state) || prev.MailingAddress.state) : prev.MailingAddress.state,
                              postalCode: d.postal_code || prev.MailingAddress.postalCode,
                              country: d.country_code || prev.MailingAddress.country,
                            }
                          }))}
                          placeholder="Street"
                          disabled={isLoading}
                        />
                        <div className="grid grid-cols-4 gap-2 max-md:grid-cols-2">
                          <input
                            type="text"
                            value={selectedContact.MailingAddress?.city || ''}
                            placeholder="City"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                MailingAddress: { ...prev.MailingAddress, city: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                          <input
                            type="text"
                            value={selectedContact.MailingAddress?.state || ''}
                            placeholder="State"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                MailingAddress: { ...prev.MailingAddress, state: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                          <input
                            type="text"
                            value={selectedContact.MailingAddress?.postalCode || ''}
                            placeholder="Zip/Postal Code"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                MailingAddress: { ...prev.MailingAddress, postalCode: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                          <input
                            type="text"
                            value={formatCountryDisplay(selectedContact.MailingAddress?.country)}
                            placeholder="Country"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                MailingAddress: { ...prev.MailingAddress, country: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                        </div>
                      </div>

                      <div>
                        <h3 className="block text-sm text-gray-600 mb-1">Alternate Address</h3>
                        <StreetAutocompleteInput
                          value={selectedContact.OtherAddress?.street || ''}
                          onChange={(text) => setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: { ...prev.OtherAddress, street: text },
                          }))}
                          onSelectSuggestion={(s) => setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: { ...prev.OtherAddress, street: s.description || s.main_text || '' },
                          }))}
                          onPlaceDetails={(d) => setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: {
                              ...prev.OtherAddress,
                              street: [d.street_number, d.route].filter(Boolean).join(' ') || prev.OtherAddress.street,
                              city: d.city || prev.OtherAddress.city,
                              state: d.country_code === 'US' ? (toUSStateCode(d.state) || prev.OtherAddress.state) : prev.OtherAddress.state,
                              postalCode: d.postal_code || prev.OtherAddress.postalCode,
                              country: d.country_code || prev.OtherAddress.country,
                            }
                          }))}
                          placeholder="Street"
                          disabled={isLoading}
                        />
                        <div className="grid grid-cols-4 gap-2 max-md:grid-cols-2">
                          <input
                            type="text"
                            value={selectedContact.OtherAddress?.city || ''}
                            placeholder="City"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                OtherAddress: { ...prev.OtherAddress, city: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                          <input
                            type="text"
                            value={selectedContact.OtherAddress?.state || ''}
                            placeholder="State"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                OtherAddress: { ...prev.OtherAddress, state: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                          <input
                            type="text"
                            value={selectedContact.OtherAddress?.postalCode || ''}
                            placeholder="Zip/Postal Code"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                OtherAddress: { ...prev.OtherAddress, postalCode: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                          <input
                            type="text"
                            value={formatCountryDisplay(selectedContact.OtherAddress?.country)}
                            placeholder="Country"
                            className="p-2 border rounded-md"
                            onChange={(e) =>
                              setSelectedContact((prev) => ({
                                ...prev,
                                OtherAddress: { ...prev.OtherAddress, country: e.target.value },
                              }))
                            }
                            disabled={isLoading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck="false"
                            data-form-type="other"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-span-3 max-md:col-span-1">
                      <label className="block text-sm text-gray-600 mb-1">Account Billing State (Short Code)</label>
                      <select
                        className="w-full p-2 border rounded-md"
                        defaultValue={selectedContact.Account_Billing_State || ""}
                        style={{ color: selectedContact.Account_Billing_State ? "black" : "gray" }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedContact((prev) => ({ ...prev, Account_Billing_State: value }));
                          e.target.style.color = value === "" ? "gray" : "black";
                        }}
                        disabled={isLoading}
                        autoComplete="new-password"
                        autoCorrect="off"
                        spellCheck="false"
                        data-form-type="other"
                      >
                        <option value="" disabled>Select the state</option>
                        {US_STATES.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
              </form>
            </div>
          )}
        </div>

        <div className="add-contact-modal-footer">
          {isSuccess ? (
            <button
              type="button"
              onClick={handleSuccessDone}
              className="add-contact-modal-submit"
            >
              Done
            </button>
          ) : mode === 'add' ? (
            <button
              type="button"
              className="add-contact-modal-submit"
              onClick={handleCreateContact}
              disabled={isLoading}
            >
              Create Contact
            </button>
          ) : (
            <button
              type="button"
              className="add-contact-modal-submit"
              onClick={handleUpdateContact}
              disabled={isLoading}
            >
              Update Contact
            </button>
          )}
        </div>
      </div>
      {showAddAccountModal && (
        <AddAccountModal
          isOpen={showAddAccountModal}
          onClose={() => setShowAddAccountModal(false)}
          onSuccess={() => {
            setShowAddAccountModal(false);
            // Optionally refresh the accounts list after adding a new account
            handleAccountSearch({ target: { value: selectedContact.AccountName } });
          }}
        />
      )}
    </div>,
    document.body
  );
}
